import { type ATTN, type ENRL } from "@/models.ts";

const THIRTY_DAY_MONTHS = new Set([3, 5, 8, 10]); // April, June, September, November (0-indexed)
const FEBRUARY = 1;

export function formatAttendanceMonth(firstOfMonth: string): string {
  if (!firstOfMonth) return "";
  const date = new Date(firstOfMonth);
  if (Number.isNaN(date.getTime())) return firstOfMonth;
  return date.toLocaleString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

function getProgramYearDateRange(
  programYear: string
): { start: string; end: string } | null {
  const match = programYear.match(/^(\d{4})-(\d{2,4})$/);
  if (!match) return null;

  const startYear = Number.parseInt(match[1], 10);
  const endYearSuffix = match[2];
  const endYear =
    endYearSuffix.length === 2
      ? Number.parseInt(`${Math.floor(startYear / 100)}${endYearSuffix}`, 10)
      : Number.parseInt(endYearSuffix, 10);

  return { start: `${startYear}-07-01`, end: `${endYear}-06-30` };
}

function addMonthToIso(monthStart: string): string {
  const [year, month] = monthStart.split("-").map(Number);
  const nextMonth = month === 12 ? 1 : month + 1;
  const nextYear = month === 12 ? year + 1 : year;
  return `${nextYear}-${String(nextMonth).padStart(2, "0")}-01`;
}

function getCurrentMonthStart(): string {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-01`;
}

export function getSelectableAttendanceMonths(
  programYear: string,
  preschoolCode: string,
  enrlRecords: ENRL[],
  attnRecords: ATTN[]
): { Text: string; Value: string }[] {
  const range = getProgramYearDateRange(programYear);
  if (!range) return [];

  const matchingEnrl = enrlRecords.find(
    (e) => e.PreschoolCode === preschoolCode && e.ProgramYear === programYear
  );
  const enrollmentMonthStart = matchingEnrl?.EnrollmentStartDate
    ? `${matchingEnrl.EnrollmentStartDate.slice(0, 7)}-01`
    : range.start;
  // These are ISO date strings, not numbers - Math.max/min would coerce to NaN.
  // eslint-disable-next-line unicorn/prefer-math-min-max
  const lowerBound =
    enrollmentMonthStart > range.start ? enrollmentMonthStart : range.start;

  const currentMonthStart = getCurrentMonthStart();
  // These are ISO date strings, not numbers - Math.max/min would coerce to NaN.
  // eslint-disable-next-line unicorn/prefer-math-min-max
  let upperBound =
    range.end < currentMonthStart ? range.end : currentMonthStart;
  if (
    matchingEnrl?.EnrollmentEndDate &&
    matchingEnrl.EnrollmentEndDate < upperBound
  ) {
    upperBound = matchingEnrl.EnrollmentEndDate;
  }

  const existingMonths = new Set(
    attnRecords
      .filter(
        (a) =>
          a.ProgramYear === programYear && a.PreschoolCode === preschoolCode
      )
      .map((a) => a.FirstOfMonth)
  );

  const months: { Text: string; Value: string }[] = [];
  let cursor = lowerBound;
  while (cursor < upperBound) {
    if (!existingMonths.has(cursor)) {
      months.push({ Text: formatAttendanceMonth(cursor), Value: cursor });
    }
    cursor = addMonthToIso(cursor);
  }

  return months.reverse();
}

export function getDaysExpectedMax(firstOfMonth: string): number {
  const date = new Date(firstOfMonth);
  if (Number.isNaN(date.getTime())) return 31;

  const month = date.getUTCMonth();
  if (month === FEBRUARY) return 29;
  if (THIRTY_DAY_MONTHS.has(month)) return 30;
  return 31;
}

export function getLastDayOfMonth(firstOfMonth: string): string {
  const date = new Date(firstOfMonth);
  if (Number.isNaN(date.getTime())) return firstOfMonth;

  const lastDay = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)
  );
  return lastDay.toISOString().slice(0, 10);
}

export function getAttendanceMonthOptions(
  programYear: string
): { Text: string; Value: string }[] {
  const range = getProgramYearDateRange(programYear);
  if (!range) return [];

  const currentMonthStart = getCurrentMonthStart();
  const upperBound =
    range.end < currentMonthStart ? range.end : currentMonthStart;

  const months: { Text: string; Value: string }[] = [];
  let cursor = range.start;
  while (cursor < upperBound) {
    months.push({ Text: formatAttendanceMonth(cursor), Value: cursor });
    cursor = addMonthToIso(cursor);
  }

  return months.reverse();
}

type DaysExpectedSumFields = Pick<
  ATTN,
  | "DaysExpected"
  | "DaysAttended"
  | "DaysAbsentExcused"
  | "DaysAbsentUnexcused"
  | "DaysAbsentSuspension"
  | "DaysAttendedSuspension"
>;

export function validateDaysExpectedSum(
  row: DaysExpectedSumFields
): true | string {
  const sum =
    Number(row.DaysAttended) +
    Number(row.DaysAbsentExcused) +
    Number(row.DaysAbsentUnexcused) +
    Number(row.DaysAbsentSuspension) +
    Number(row.DaysAttendedSuspension);

  return Number(row.DaysExpected) === sum
    ? true
    : "Days Expected must equal the sum of Days Attended, Days Absent (Excused), Days Absent (Unexcused), Days Absent (Suspension), and Days Attended (Suspension)";
}

function validateNonNegativeInteger(value: string | number) {
  if (typeof value === "string" && value.trim() === "") {
    return "Must be a non-negative whole number";
  }
  const n = Number(value);
  if (!Number.isInteger(n) || n < 0) {
    return "Must be a non-negative whole number";
  }
  return true;
}

function validateOptionalNonNegativeInteger(value: string | number) {
  if (typeof value === "string" && value.trim() === "") return true;
  return validateNonNegativeInteger(value);
}

export function useATTNFormLogic(firstOfMonth: string) {
  const maxDaysExpected = getDaysExpectedMax(firstOfMonth);

  const validateDaysExpected = {
    integer: (value: string | number) => {
      const n = Number(value);
      return Number.isInteger(n) && n >= 0
        ? true
        : "Must be a non-negative whole number";
    },
    max: (value: string | number) => {
      const n = Number(value);
      return n <= maxDaysExpected
        ? true
        : `Days Expected cannot exceed ${maxDaysExpected} for this month`;
    },
  };

  return {
    maxDaysExpected,
    validators: {
      validateNonNegativeInteger,
      validateOptionalNonNegativeInteger,
      validateDaysExpected,
    },
  };
}
